import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Clock, Upload } from 'lucide-react';
import { SubcontratasPanel } from '../components/SubcontratasPanel';

function EmpCard({ emp, delay, onAssignShift, canEditShift }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      style={{
        background: '#1e293b', border: '1px solid #334155',
        borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '10px',
        transition: 'border-color 200ms, box-shadow 200ms'
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#475569'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
            background: emp.active ? 'linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)' : '#334155',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '14px', fontWeight: 700
          }}>
            {((emp.nombre || emp.numero_personal || '?')[0] || '?').toUpperCase()}
          </div>
          <div>
            <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}>
              {emp.nombre && emp.apellidos ? `${emp.apellidos}, ${emp.nombre}` : emp.numero_personal}
            </div>
            <div style={{ color: '#475569', fontSize: '11px', fontFamily: 'IBM Plex Mono' }}>#{emp.numero_personal}</div>
          </div>
        </div>
        <span style={{
          background: emp.active ? '#22c55e18' : '#64748b18',
          color: emp.active ? '#22c55e' : '#64748b',
          border: `1px solid ${emp.active ? '#22c55e30' : '#64748b30'}`,
          borderRadius: '20px', padding: '2px 10px',
          fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap'
        }}>
          {emp.active ? 'Activo' : 'Inactivo'}
        </span>
      </div>

      {/* Details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {emp.desc_puesto && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{ color: '#475569', fontSize: '11px', minWidth: '60px' }}>Puesto:</span>
            <span style={{ color: '#94a3b8', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.desc_puesto}</span>
          </div>
        )}
        {emp.desc_organizacion_1 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{ color: '#475569', fontSize: '11px', minWidth: '60px' }}>Org.:</span>
            <span style={{ color: '#94a3b8', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.desc_organizacion_1}</span>
          </div>
        )}
        {emp.desc_centro_coste && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{ color: '#475569', fontSize: '11px', minWidth: '60px' }}>Centro:</span>
            <span style={{ color: '#94a3b8', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.desc_centro_coste}</span>
          </div>
        )}
      </div>

      {emp.last_seen && (
        <div style={{ color: '#334155', fontSize: '11px', fontFamily: 'IBM Plex Mono', borderTop: '1px solid #0f172a', paddingTop: '8px' }}>
          Último CSV: {emp.last_seen}
        </div>
      )}
      {canEditShift && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '4px' }}>
          {emp.assigned_shift?.shifts?.length > 0 ? (
            <span title={emp.assigned_shift.shifts.map(s => `${s.name} ${String(s.start_hour).padStart(2,'0')}:${String(s.start_minute||0).padStart(2,'0')}→${String(s.end_hour).padStart(2,'0')}:${String(s.end_minute||0).padStart(2,'0')}`).join(' · ')}
              style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.45)', color: '#c4b5fd', fontFamily: 'IBM Plex Mono', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Clock size={11} /> Turno asignado ({emp.assigned_shift.shifts.length})
            </span>
          ) : (
            <span style={{ fontSize: '11px', color: '#475569' }}>Sin turno fijo</span>
          )}
          <Button size="sm" variant="outline" data-testid={`assign-shift-${emp.numero_personal}`} onClick={() => onAssignShift(emp)} style={{ fontSize: '11px', padding: '4px 10px', height: 'auto' }}>
            {emp.assigned_shift?.shifts?.length > 0 ? 'Editar turno' : 'Asignar turno'}
          </Button>
        </div>
      )}
    </motion.div>
  );
}

export default function EmpleadosPage() {
  const { apiFetch, user } = useAuth();
  const { t } = useLang();
  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  // Assigned shift modal state
  const [shiftEmp, setShiftEmp]       = useState(null);
  const [shiftBands, setShiftBands]   = useState([]);
  const [shiftSaving, setShiftSaving] = useState(false);
  // Subcontratas import modal
  const [showSubcontratasModal, setShowSubcontratasModal] = useState(false);

  const openAssignShift = (emp) => {
    setShiftEmp(emp);
    setShiftBands(emp.assigned_shift?.shifts?.length > 0
      ? emp.assigned_shift.shifts.map(s => ({ ...s }))
      : [{ name: 'Mañana', start_hour: 6, start_minute: 0, end_hour: 14, end_minute: 0, weekdays: null }]
    );
  };
  const saveAssignShift = async () => {
    if (!shiftEmp) return;
    setShiftSaving(true);
    try {
      const res = await apiFetch(`/api/employees/${shiftEmp.numero_personal}/assigned-shift`, {
        method: 'PUT',
        body: JSON.stringify({
          shifts: shiftBands.map(b => ({
            name: b.name,
            start_hour: Number(b.start_hour),
            start_minute: Number(b.start_minute) || 0,
            end_hour: Number(b.end_hour),
            end_minute: Number(b.end_minute) || 0,
            weekdays: b.weekdays || null,
          })),
        }),
      });
      if (res.ok) {
        toast.success('Turno asignado');
        setShiftEmp(null);
        load(page);
      } else {
        let detail = `Error ${res.status}`;
        try { const err = await res.json(); if (err?.detail) detail = err.detail; }
        catch (_parseErr) { /* response body not JSON — keep generic message */ }
        toast.error(detail);
      }
    } catch (e) {
      toast.error(e?.message === 'Sesión expirada' ? 'Sesión caducada' : 'Error de conexión');
    }
    setShiftSaving(false);
  };
  const clearAssignShift = async () => {
    if (!shiftEmp) return;
    if (!window.confirm('¿Quitar el turno asignado a este empleado? Volverá a usar las franjas del centro o la clasificación global.')) return;
    setShiftSaving(true);
    try {
      const res = await apiFetch(`/api/employees/${shiftEmp.numero_personal}/assigned-shift`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Turno eliminado');
        setShiftEmp(null);
        load(page);
      }
    } catch (err) { console.error('Clear assigned shift failed:', err); toast.error('Error de conexión'); }
    setShiftSaving(false);
  };

  const load = useCallback(async (p = 1, s = search, af = activeFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 40 });
      if (s) params.append('search', s);
      if (af !== null) params.append('active', af);
      const res  = await apiFetch(`/api/employees?${params}`);
      const json = await res.json();
      setData(json.data || []);
      setTotal(json.total || 0);
      setPages(json.pages || 1);
      setPage(p);
    } catch (err) { console.warn("[SmartClockIn] silent catch:", err); }
    setLoading(false);
  }, [apiFetch, search, activeFilter]);

  useEffect(() => { load(1); }, [activeFilter]);

  const handleSearch = (e) => { e.preventDefault(); load(1, search, activeFilter); };

  return (
    <div style={{ padding: '32px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{t('empleados')}</h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>{total.toLocaleString('es-ES')} empleados</p>
        </div>
        <Button
          onClick={() => setShowSubcontratasModal(true)}
          data-testid="empleados-import-subcontratas-btn"
          style={{
            background: '#1e293b',
            border: '1px solid #475569',
            color: '#f1f5f9',
            height: '38px',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 500,
          }}
        >
          <Upload size={15} />
          Importar Subcontratas
        </Button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '300px' }}>
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('buscar')}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
          />
          <Button type="submit" size="sm" style={{ background: '#2563eb', color: 'white', height: '36px', whiteSpace: 'nowrap' }}>Buscar</Button>
        </form>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[null, true, false].map((val) => {
            const label = val === null ? 'Todos' : val ? t('activo') : t('inactivo');
            const active = activeFilter === val;
            return (
              <button
                key={String(val)}
                onClick={() => setActiveFilter(val)}
                style={{
                  background: active ? '#334155' : '#1e293b',
                  border: `1px solid ${active ? '#475569' : '#334155'}`,
                  color: active ? '#f1f5f9' : '#64748b',
                  borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px',
                  transition: 'background-color 200ms, color 200ms, border-color 200ms'
                }}
              >{label}</button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {[...Array(12)].map((_, i) => (
            <div key={i} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', height: '140px', animation: 'pulse 2s ease-in-out infinite' }} />
          ))}
          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '80px 20px' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{ margin: '0 auto 16px', display: 'block' }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <p style={{ margin: 0, fontSize: '16px', color: '#475569' }}>Sin empleados en el directorio</p>
          <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#334155' }}>Ejecuta una sincronización SFTP para cargar el directorio</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {data.map((emp, i) => (
            <EmpCard
              key={emp.numero_personal || i}
              emp={emp}
              delay={i * 0.02}
              onAssignShift={openAssignShift}
              canEditShift={user?.role === 'admin' || (user?.allowed_centers || []).includes(emp.desc_centro_coste)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
          <Button
            variant="outline" size="sm"
            disabled={page <= 1}
            onClick={() => load(page - 1)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: page <= 1 ? '#334155' : '#94a3b8' }}
          >Anterior</Button>
          <span style={{ color: '#64748b', fontSize: '13px' }}>{t('pagina')} {page} {t('de')} {pages}</span>
          <Button
            variant="outline" size="sm"
            disabled={page >= pages}
            onClick={() => load(page + 1)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: page >= pages ? '#334155' : '#94a3b8' }}
          >Siguiente</Button>
        </div>
      )}

      {/* ASSIGN-SHIFT MODAL */}
      {shiftEmp && (
        <div data-testid="assign-shift-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }} onClick={() => !shiftSaving && setShiftEmp(null)}>
          <Card onClick={e => e.stopPropagation()} style={{ background: '#1e293b', border: '1px solid #334155', padding: 24, maxWidth: 560, width: '100%', maxHeight: '88vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>Asignar turno individual</h3>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18 }}>
              {(shiftEmp.apellidos || '') + ', ' + (shiftEmp.nombre || '')} · #{shiftEmp.numero_personal} · {shiftEmp.desc_centro_coste || 'Sin centro'}
            </p>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
              Para <strong>ampliadores</strong> u otros casos especiales. El turno aquí asignado sobrescribe la plantilla del centro.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {shiftBands.map((b, idx) => (
                <Card key={idx} style={{ background: '#0f172a', border: '1px solid #334155', padding: 12, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 130px' }}>
                    <Label style={{ color: '#94a3b8', fontSize: 11 }}>Nombre</Label>
                    <select value={b.name} onChange={e => setShiftBands(bb => bb.map((x,i) => i===idx?{...x,name:e.target.value}:x))} style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
                      <option>Mañana</option><option>Tarde</option><option>Noche</option><option>Partido</option>
                    </select>
                  </div>
                  {['start_hour','start_minute','end_hour','end_minute'].map(k => (
                    <div key={k} style={{ flex: '0 0 70px' }}>
                      <Label style={{ color: '#94a3b8', fontSize: 11 }}>{k.replace('_',' ')}</Label>
                      <input type="number" min={0} max={k.includes('hour')?23:59} value={b[k] ?? 0}
                        onChange={e => setShiftBands(bb => bb.map((x,i) => i===idx?{...x,[k]:parseInt(e.target.value||'0',10)}:x))}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'IBM Plex Mono' }} />
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => setShiftBands(bb => bb.filter((_,i) => i!==idx))} style={{ color: '#f87171' }}>✕</Button>
                </Card>
              ))}
              <Button variant="outline" size="sm" onClick={() => setShiftBands(bb => [...bb, { name: 'Tarde', start_hour: 14, start_minute: 0, end_hour: 22, end_minute: 0, weekdays: null }])} style={{ alignSelf: 'flex-start' }}>+ Añadir franja</Button>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'space-between' }}>
              {shiftEmp.assigned_shift?.shifts?.length > 0 ? (
                <Button variant="ghost" onClick={clearAssignShift} disabled={shiftSaving} style={{ color: '#f87171' }} data-testid="clear-assign-shift">Quitar turno</Button>
              ) : <div />}
              <div style={{ display: 'flex', gap: 10 }}>
                <Button variant="ghost" onClick={() => setShiftEmp(null)} disabled={shiftSaving}>Cancelar</Button>
                <Button onClick={saveAssignShift} disabled={shiftSaving || shiftBands.length===0} data-testid="save-assign-shift">{shiftSaving?'Guardando…':'Guardar'}</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
      {/* SUBCONTRATAS IMPORT MODAL */}
      {showSubcontratasModal && (
        <div
          data-testid="subcontratas-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}
          onClick={() => setShowSubcontratasModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}
          >
            <button
              onClick={() => setShowSubcontratasModal(false)}
              data-testid="subcontratas-modal-close"
              style={{
                position: 'absolute', top: 12, right: 16, background: 'transparent',
                border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer', zIndex: 1, lineHeight: 1,
              }}
              aria-label="Cerrar"
            >×</button>
            <SubcontratasPanel
              compact
              onImported={() => { load(page); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
